import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

/**
 * Exit-intent lead capture. Mounted globally; self-gates by pathname so the
 * modal only appears where a reader might be leaving with intent still warm:
 * Landing, Pricing, FAQ, Sample Memo. Shown at most once per 24 hours per
 * browser (localStorage timestamp, shared across tabs and visits) and only
 * after 8s on the page, so quick bouncers and clicks-to-nav don't fire it.
 *
 * Trigger: cursor exits the top edge of the viewport (desktop). We do not
 * attempt to trigger on mobile because there's no comparable signal.
 */

const ELIGIBLE_PATHS = new Set(["/", "/pricing", "/faq"]);
const SHOWN_AT_KEY = "w3rc_exit_modal_shown_at_v2";
const SHOW_INTERVAL_MS = 24 * 60 * 60 * 1000; // once per day
const ARM_DELAY_MS = 8000;

const shownWithinInterval = () => {
  try {
    const at = Number(localStorage.getItem(SHOWN_AT_KEY) || 0);
    return at > 0 && Date.now() - at < SHOW_INTERVAL_MS;
  } catch (err) {
    // storage may be unavailable in private browsing; treat as not-seen
    console.debug("exit-intent: localStorage unavailable", err);
    return false;
  }
};

export default function ExitIntentModal() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [challenge, setChallenge] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const armedRef = useRef(false);
  const mountedAt = useRef(Date.now());

  const eligible = ELIGIBLE_PATHS.has(pathname);

  // Fetch a challenge lazily once the modal opens (avoids extra requests on all pages).
  const loadChallenge = useCallback(async () => {
    try {
      const res = await api.get("/checklist/challenge");
      setChallenge(res.data.challenge_token);
    } catch (err) {
      // Fetch it lazily; surfaced on submit if still missing.
      console.debug("exit-intent: challenge fetch failed", err);
    }
  }, []);

  useEffect(() => {
    if (!eligible) return undefined;

    // Skip if the modal already appeared for this browser in the last 24h.
    if (shownWithinInterval()) return undefined;

    let armTimer = null;

    const onLeave = (e) => {
      if (!armedRef.current || open) return;
      // Fired only when the cursor exits through the top edge, not sidebars.
      if (e.clientY > 10) return;
      if (e.relatedTarget || e.toElement) return;
      if (shownWithinInterval()) return; // another tab may have shown it since arming
      armedRef.current = false;
      try { localStorage.setItem(SHOWN_AT_KEY, String(Date.now())); } catch (err) { console.debug("exit-intent: could not persist shown-at", err); }
      track("exit_intent_shown", { path: pathname });
      setOpen(true);
      loadChallenge();
    };

    armTimer = setTimeout(() => {
      armedRef.current = true;
    }, ARM_DELAY_MS);

    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => {
      if (armTimer) clearTimeout(armTimer);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, [eligible, open, pathname, loadChallenge]);

  // ESC to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal("dismiss_escape");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function closeModal(reason) {
    track("exit_intent_dismissed", { reason, path: pathname });
    setOpen(false);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !org.trim()) {
      toast.error("Name, email, and firm are required.");
      return;
    }
    if (!challenge) {
      toast.error("Verification token missing. Please try again.");
      loadChallenge();
      return;
    }
    if (Date.now() - mountedAt.current < 1500) {
      toast.error("Please take a moment to complete the form.");
      return;
    }
    setSending(true);
    try {
      await api.post("/checklist/lead", {
        email: email.trim(),
        full_name: name.trim(),
        organization: org.trim(),
        challenge_token: challenge,
        website,
      });
      track("exit_intent_submitted", { path: pathname });
      setSent(true);
      toast.success("Checklist and sample memo sent. Check your inbox.");
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error("Too many requests. Please wait a few minutes.");
      else if (status === 400) {
        toast.error(err.response?.data?.detail || "Verification failed.");
        loadChallenge();
      } else toast.error(err.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/70 backdrop-blur-sm"
      onClick={() => closeModal("backdrop_click")}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[560px] bg-paper border border-ink shadow-2xl"
      >
        <button
          data-testid="exit-intent-close"
          onClick={() => closeModal("close_button")}
          className="absolute top-3 right-3 p-2 text-ink/75 hover:text-ink transition-colors"
          aria-label="Close"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        {!sent ? (
          <div className="p-8 lg:p-10">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blueprint mb-3">
              Before you go
            </div>
            <h2
              id="exit-intent-title"
              className="font-serif text-3xl sm:text-4xl tracking-tighter font-semibold leading-[1.02] text-ink"
            >
              Take the framework with you.
            </h2>
            {/* One paragraph, founder's revision Feb 2026: the count and the
                proof in a single line, so the reader is not asked to hold two
                document descriptions before reaching the form. Keeps the lead
                paragraph's size and weight; the smaller second paragraph that
                itemized the documents is retired. */}
            <p data-testid="exit-intent-body" className="font-mono text-[15px] sm:text-base text-ink leading-relaxed mt-4">
              The Clarity Issue-Mapping Starter Kit. Two free resources that show you exactly what a real compliance analysis looks like.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-3" noValidate>
              {/* honeypot */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                className="absolute left-[-9999px] w-px h-px"
                aria-hidden="true"
              />
              <input
                data-testid="exit-intent-name"
                type="text"
                required
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-ink font-mono text-sm text-ink placeholder:text-ink/60 focus:outline-none focus:border-blueprint"
              />
              <input
                data-testid="exit-intent-email"
                type="email"
                required
                placeholder="Work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-ink font-mono text-sm text-ink placeholder:text-ink/60 focus:outline-none focus:border-blueprint"
              />
              <input
                data-testid="exit-intent-org"
                type="text"
                required
                placeholder="Firm or organization"
                value={org}
                onChange={(e) => setOrg(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-ink font-mono text-sm text-ink placeholder:text-ink/60 focus:outline-none focus:border-blueprint"
              />
              <button
                data-testid="exit-intent-submit"
                type="submit"
                disabled={sending || !challenge}
                className="w-full mt-2 px-5 py-3.5 font-mono text-xs uppercase tracking-[0.2em] bg-blueprint text-white border border-blueprint hover:bg-ink hover:border-ink transition-colors disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send both →"}
              </button>
              {/* Second path for price-hesitant leavers: the $297 solo run. */}
              <div className="font-mono text-xs text-ink/80 pt-2 text-center leading-relaxed">
                Ready to map your own situation?{" "}
                <Link
                  to="/register?plan=single"
                  data-testid="exit-intent-solo-link"
                  onClick={() => {
                    track("single_pass_checkout_click", { source: "exit_intent" });
                    setOpen(false);
                  }}
                  className="text-blueprint underline underline-offset-2 hover:text-ink transition-colors"
                >
                  Clarity SOLO™ is $297
                </Link>
                , credited in full toward Clarity EXPLORE™ when you upgrade within 7 days of first use.
              </div>
              {/* Secondary by design: required, read last, never competing with
                  the offer above it. */}
              <div data-testid="exit-intent-disclaimer" className="mt-1 pt-3 border-t border-ink/15 font-mono text-[10px] leading-relaxed text-ink/55">
                A resource from Dr. Tonya M. Evans, lawyer and global digital assets regulatory strategist. For educational and issue-identification purposes only. Not legal advice. No attorney-client relationship.
              </div>
            </form>
          </div>
        ) : (
          <div data-testid="exit-intent-success" className="p-8 lg:p-10">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blueprint mb-3">
              Delivered
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tighter font-semibold leading-[1.02] text-ink">
              Check your inbox.
            </h2>
            <p className="font-mono text-sm text-ink/85 leading-relaxed mt-4">
              Both PDFs are on the way. If they don&rsquo;t arrive in a few minutes, check the spam or promotions folder, then whitelist the sender.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/pricing"
                onClick={() => track("exit_intent_success_cta_pricing")}
                className="inline-flex items-center px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] bg-blueprint text-white border border-blueprint hover:bg-ink hover:border-ink transition-colors"
              >
                See EXPLORE pricing →
              </Link>
              <button
                onClick={() => closeModal("success_close")}
                className="inline-flex items-center px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-ink text-ink hover:bg-ink hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
